import { useCallback, useLayoutEffect } from 'react';
import {
  type NavigateOptions,
  type To,
  useLocation,
  useNavigate,
} from 'react-router-dom';

const MAIN_SCROLL_CONTAINER_ID = 'app-main-scroll';
const STORAGE_PREFIX = 'list-return-scroll:';

const getMainScrollContainer = () =>
  document.getElementById(MAIN_SCROLL_CONTAINER_ID);

type SavedScrollPosition = {
  main: number;
  window: number;
  documentElement: number;
  body: number;
};

const readSavedPosition = (raw: string): SavedScrollPosition | null => {
  const parsedNumber = Number(raw);
  if (Number.isFinite(parsedNumber)) {
    return {
      main: parsedNumber,
      window: 0,
      documentElement: 0,
      body: 0,
    };
  }

  try {
    const parsed = JSON.parse(raw) as Partial<SavedScrollPosition>;
    return {
      main: Number(parsed.main) || 0,
      window: Number(parsed.window) || 0,
      documentElement: Number(parsed.documentElement) || 0,
      body: Number(parsed.body) || 0,
    };
  } catch {
    return null;
  }
};

const getCurrentPosition = (): SavedScrollPosition => ({
  main: getMainScrollContainer()?.scrollTop ?? 0,
  window: window.scrollY || 0,
  documentElement: document.documentElement?.scrollTop ?? 0,
  body: document.body?.scrollTop ?? 0,
});

const getPrimarySavedTop = (position: SavedScrollPosition) =>
  Math.max(
    position.main,
    position.window,
    position.documentElement,
    position.body,
  );

const restorePosition = (position: SavedScrollPosition) => {
  const main = getMainScrollContainer();
  if (main) main.scrollTop = position.main;

  window.scrollTo(0, position.window);
  if (document.documentElement) {
    document.documentElement.scrollTop = position.documentElement;
  }
  if (document.body) {
    document.body.scrollTop = position.body;
  }
};

const getCurrentTopForPosition = (position: SavedScrollPosition) =>
  position.main > 0
    ? getMainScrollContainer()?.scrollTop ?? 0
    : Math.max(
        window.scrollY || 0,
        document.documentElement?.scrollTop ?? 0,
        document.body?.scrollTop ?? 0,
      );

/**
 * Preserves the app main scroll position when navigating from a list to detail.
 *
 * Most pages scroll inside #app-main-scroll, but a few layout/browser cases can
 * move the document instead. Save both axes so list pages can opt into reliable
 * restoration after returning from detail.
 */
export const useListReturnNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const currentUrl = `${location.pathname}${location.search}`;
  const storageKey = `${STORAGE_PREFIX}${currentUrl}`;

  useLayoutEffect(() => {
    const saved = sessionStorage.getItem(storageKey);
    if (!saved) return;

    const position = readSavedPosition(saved);
    if (!position) {
      sessionStorage.removeItem(storageKey);
      return;
    }

    let timeoutId: number | undefined;
    let frameId: number | undefined;
    const startedAt = Date.now();
    const primaryTop = getPrimarySavedTop(position);

    const restore = () => {
      restorePosition(position);

      const currentTop = getCurrentTopForPosition(position);
      const restored = Math.abs(currentTop - primaryTop) <= 2;

      if (restored || Date.now() - startedAt > 2500) {
        sessionStorage.removeItem(storageKey);
        return;
      }

      timeoutId = window.setTimeout(restore, 50);
    };

    frameId = requestAnimationFrame(restore);

    return () => {
      if (frameId !== undefined) cancelAnimationFrame(frameId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [storageKey]);

  const saveScroll = useCallback(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(getCurrentPosition()));
  }, [storageKey]);

  const navigateFromList = useCallback(
    (to: To, options?: NavigateOptions) => {
      saveScroll();
      navigate(to, {
        ...options,
        state: {
          ...(typeof options?.state === 'object' && options.state !== null
            ? options.state
            : {}),
          from: currentUrl,
        },
      });
    },
    [currentUrl, navigate, saveScroll],
  );

  return {
    saveScroll,
    navigateFromList,
  };
};
